import { asDate } from 'owa-datetime';
import { getTimeZoneOffset } from 'owa-datetime-store';
import type { DayOfWeek, GetScheduleWorkingHours } from 'owa-graph-schema';
import type WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';
import { getUserConfiguration } from 'owa-session-store';
import { observableNow } from 'owa-observable-datetime';

const daysOfWeek: DayOfWeek[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];

export default function convertWorkingHoursTypeToGetScheduleWorkingHours(
    workingHours: WorkingHoursType
): GetScheduleWorkingHours {
    const now = observableNow();

    const { WorkingHoursTimeZoneId } = workingHours;

    const offsetTimeZone =
        getUserConfiguration().UserOptions?.TimeZone ?? WorkingHoursTimeZoneId ?? '';

    const { WorkDays, WorkHoursStartTimeInMinutes, WorkHoursEndTimeInMinutes } =
        processWorkingHours(workingHours, asDate(now), offsetTimeZone);

    return {
        daysOfWeek: WorkDays ? createWorkWeekDaysArray(WorkDays) : undefined,
        startTime:
            WorkHoursStartTimeInMinutes !== undefined && WorkHoursStartTimeInMinutes !== null
                ? convertMinutesToString(WorkHoursStartTimeInMinutes)
                : '',
        endTime: WorkHoursEndTimeInMinutes ? convertMinutesToString(WorkHoursEndTimeInMinutes) : '',
        timeZone: {
            name: WorkingHoursTimeZoneId ?? '',
        },
    };
}

function convertMinutesToString(minutes: number) {
    const hours = Math.floor(minutes / 60)
        .toString()
        .padStart(2, '0');
    const seconds = (minutes % 60).toString().padStart(2, '0');

    return `${hours}:${seconds}:00`;
}

/**
 * Converts a bit mask of work days to a day of week array. Assumes that the bitmask has the lowest
 * order bit correspond to sunday, and go upwards from there. Format sat|fri|thurs|wed|tues|mon|sun
 *
 * for example, passing in 22 = 0010110, which results in [mon, tues, thurs]
 * @param workDays The bitmask for the work week days
 */
function createWorkWeekDaysArray(workDays: number): DayOfWeek[] {
    const result: DayOfWeek[] = [];
    for (let day = 0; day < 7; day++) {
        if (workDays & (1 << day)) {
            result.push(daysOfWeek[day]);
        }
    }
    return result;
}

/**
 * Working hours received from GetUserAvailabilityInternal call are represented in UTC however
 * the time zone offsets are applied. eg: 9:00 am Pacific Standard Time reflects 960 (Difference in minutes from UTC 0)
 * However, working hours received from GetOwaUserConfiguration call do not have the time zone
 * offsets applied. eg: 9:00 am Pacific Standard Time reflects 540 (Difference in minutes from UTC 0)
 * In order to display the non working hour blocks correctly we need to remove the time zone
 * offsets applied to the response of GetUserAvailabilityInternal to convert 960 to 540 which is
 * done in this method.
 * @param workingHours WorkingHours of the attendee received from GetUserAvailabilityInternal service call
 * @param date Current date
 * @param timeZone Current time zone in which the user is present
 */
const processWorkingHours = (
    workingHours: WorkingHoursType,
    date: Date,
    timeZone: string
): WorkingHoursType => {
    let { WorkDays, WorkHoursStartTimeInMinutes, WorkHoursEndTimeInMinutes } = workingHours;

    if (WorkHoursStartTimeInMinutes && WorkHoursEndTimeInMinutes) {
        const offset = getTimeZoneOffset(date, timeZone);
        WorkHoursStartTimeInMinutes += offset;
        WorkHoursEndTimeInMinutes += offset;
    }

    return {
        WorkDays,
        WorkHoursStartTimeInMinutes,
        WorkHoursEndTimeInMinutes,
    };
};
